import { ApolloClient, ApolloProvider, createHttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GlobalStyles, ThemeProvider } from '@m/alchemy-ui';
import fetch from 'cross-fetch';
import { enableES5 } from 'immer';
import { ErrorBoundary } from 'react-error-boundary';
import { DialogProvider, PageMetadataProvider, RuntimeProvider } from '../context';
import { getCommunityTheme } from './theme';
import { type AppProps } from './interfaces';
import { ErrorFallback } from './components';
import { CrowdicityGlobalStyle } from './CrowdicityGlobalStyle';

enableES5();

const createApolloClient = ({
  api,
  pkey,
  sessionId,
  sessionTimeout
}: Pick<AppProps, 'api' | 'pkey' | 'sessionId' | 'sessionTimeout'>) => {
  const authLink = setContext((_, { headers }) => ({
    headers: {
      ...headers,
      'x-crowdicity-pkey': pkey,
      'x-crowdicity-session-id': sessionId,
      'x-crowdicity-session-timeout': sessionTimeout
    }
  }));
  const GRAPHQL_URI = '/graphql';
  return new ApolloClient({
    uri: GRAPHQL_URI,
    cache: new InMemoryCache({
      typePolicies: {
        User: {
          fields: {
            socialLinks: {
              merge: false
            }
          }
        }
      }
    }),
    link: authLink.concat(
      createHttpLink({
        fetch,
        uri: api
      })
    )
  });
};

export const AdmWrapper = (props: AppProps) => {
  const api = props.api || 'http://localhost:3000/graphql';
  const apiV2Url = String(props.apiV2Url);
  const sessionId = String(props.sessionId) || '0';
  const sessionTimeout = String(props.sessionTimeout) || '0';
  const pkey = String(props.pkey);

  const handleError = (error: Error) => {
    console.log(error);
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleError}>
      <RuntimeProvider
        value={{
          ...props,
          api,
          apiV2Url,
          pkey,
          sessionId,
          sessionTimeout
        }}
      >
        <ApolloProvider
          client={createApolloClient({
            api,
            pkey,
            sessionId,
            sessionTimeout
          })}
        >
          <PageMetadataProvider>
            <ThemeProvider
              theme={getCommunityTheme({
                communityColors: props.community.colors
              })}
            >
              <DialogProvider>
                <GlobalStyles globalCss={CrowdicityGlobalStyle} />
                {props.children}
              </DialogProvider>
            </ThemeProvider>
          </PageMetadataProvider>
        </ApolloProvider>
      </RuntimeProvider>
    </ErrorBoundary>
  );
};
